import CustomCalendar from '@/components/CustomComponent/CustomCalendar/CustomCalendar';
import CustomDropdown from '@/components/CustomComponent/CustomDropdown/CustomDropdown';
import CustomLineChart from '@/components/CustomComponent/CustomLineChart';
import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import { COMMON_TEXT } from '@/helpers/common-text';
import {
  ATTENDANCE_RATE_LEGEND,
  LINE_CHART_COLOR_LIST,
  QUERY_STRING_STORE,
} from '@/helpers/constants';
import { addDays, compareAsc, format, isBefore, isSameDay } from 'date-fns';
import { round } from 'lodash';
import debounce from 'lodash/debounce';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { SelectButton } from 'primereact/selectbutton';
import { Tooltip } from 'primereact/tooltip';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { generateRandomString, getRandomHexColor } from 'src/helpers/utility';
import {
  fetchEmployeeAttendanceRateByDayForBranch,
  fetchEmployeeAttendanceRateByDayForDepartment,
  fetchEmployeeAttendanceRateByDayForFloor,
  fetchEmployeeAttendanceRateByHourForBranch,
  fetchEmployeeAttendanceRateByHourForDepartment,
  fetchEmployeeAttendanceRateByHourForFloor,
  fetchLatestTimestampListByHourForBranch,
  fetchLatestTimestampListByHourForDepartment,
  fetchLatestTimestampListByHourForFloor,
} from './query-request';
import { Checkbox } from 'primereact/checkbox';

const LOCATION_TYPE = {
  BRANCH: 'branch',
  FLOOR: 'floor',
  DEPARTMENT: 'department',
};

const HISTORY_TYPE = {
  DAY: 'day',
  HOUR: 'hour',
};

const DATE_PICKER_FIELD = {
  SELECTED_DATE: 'selectedDate',
  FROM_DATE: 'fromSelectedDate',
  TO_DATE: 'toSelectedDate',
};

const DEFAULT_FILTER_DATA = {
  // if type is HOUR => use selectedTimeRange
  // if type is DAY => use fromSelectedDate & toSelectedDate
  selectedHistoryType: HISTORY_TYPE.HOUR,
  [DATE_PICKER_FIELD.SELECTED_DATE]: new Date(),
  [DATE_PICKER_FIELD.FROM_DATE]: addDays(new Date(), -7),
  [DATE_PICKER_FIELD.TO_DATE]: addDays(new Date(), -1),
  selectedTimeRange: {
    fromTime: '00:00',
    toTime: '01:00',
  },
  locationOptions: [-1], // If contain -1, then it is initial
  selectedLocations: [],
};

@connect(state => ({
  querystring: state.querystring,
  sessionStore: state.session,
}))
class EmployeeAttendance extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tenantId:
        this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0],
      branchData: {
        chartData: [],
        tableData: [],
        filterData: { ...DEFAULT_FILTER_DATA },
      },
      floorData: {
        chartData: [],
        tableData: [],
        filterData: { ...DEFAULT_FILTER_DATA },
      },
      departmentData: {
        chartData: [],
        tableData: [],
        filterData: { ...DEFAULT_FILTER_DATA },
      },
      isLoadingLineChart: false,
      isLoadingLocation: false,
      selectedLocationType: LOCATION_TYPE.BRANCH,
      dropdownOpen: false,
    };
    this.timeRangeOptions = [
      { label: '01:00', value: '01:00' },
      { label: '02:00', value: '02:00' },
      { label: '03:00', value: '03:00' },
      { label: '04:00', value: '04:00' },
      { label: '05:00', value: '05:00' },
      { label: '06:00', value: '06:00' },
      { label: '07:00', value: '07:00' },
      { label: '08:00', value: '08:00' },
      { label: '09:00', value: '09:00' },
      { label: '10:00', value: '10:00' },
      { label: '11:00', value: '11:00' },
      { label: '12:00', value: '12:00' },
      { label: '13:00', value: '13:00' },
      { label: '14:00', value: '14:00' },
      { label: '15:00', value: '15:00' },
      { label: '16:00', value: '16:00' },
      { label: '17:00', value: '17:00' },
      { label: '18:00', value: '18:00' },
      { label: '19:00', value: '19:00' },
      { label: '20:00', value: '20:00' },
      { label: '21:00', value: '21:00' },
      { label: '22:00', value: '22:00' },
      { label: '23:00', value: '23:00' },
    ];
    this.fromTimeRangeOptions = [
      { label: '00:00', value: '00:00' },
      ...this.timeRangeOptions,
    ];
    this.toTimeRangeOptions = [
      ...this.timeRangeOptions,
      { label: '23:59', value: '23:59' },
    ];
    this.locationTypeOptions = [
      {
        label: COMMON_TEXT.BRANCH,
        value: LOCATION_TYPE.BRANCH,
      },
      {
        label: COMMON_TEXT.FLOOR,
        value: LOCATION_TYPE.FLOOR,
      },
      {
        label: COMMON_TEXT.DEPARTMENT,
        value: LOCATION_TYPE.DEPARTMENT,
      },
    ];
    this.historyTypeOptions = [
      {
        label: COMMON_TEXT.BY_TIME,
        value: HISTORY_TYPE.HOUR,
      },
      {
        label: COMMON_TEXT.BY_DAY,
        value: HISTORY_TYPE.DAY,
      },
    ];
    this.tooltipRef = React.createRef();
    this.dropdownRef = React.createRef();
  }

  getDefaultTimeRange = () => {
    const currentDate = new Date();
    let currentHour = currentDate.getHours();
    currentHour = currentHour < 10 ? `0${currentHour}` : `${currentHour}`;
    const fromTime = `00:00`;
    const toTime = `${currentHour}:00`;
    return { fromTime, toTime };
  };

  componentDidMount() {
    const { tenantId } = this.state;
    const { fromTime, toTime } = this.getDefaultTimeRange();
    const newFilterData = {
      selectedTimeRange: {
        fromTime,
        toTime,
      },
    };
    const branchData = this.updateDataByLocationType({
      locationType: LOCATION_TYPE.BRANCH,
      filterData: newFilterData,
    });
    const floorData = this.updateDataByLocationType({
      locationType: LOCATION_TYPE.FLOOR,
      filterData: newFilterData,
    });
    const departmentData = this.updateDataByLocationType({
      locationType: LOCATION_TYPE.DEPARTMENT,
      filterData: newFilterData,
    });
    this.setState({ ...branchData, ...floorData, ...departmentData }, () => {
      if (tenantId) {
        this.getCurrentAttendanceRateByLocation();
        this.getLineChartData();
      }
    });

    // Simple click outside handler
    document.addEventListener('click', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  handleClickOutside = (event) => {
    // Only handle if dropdown is open
    if (!this.state.dropdownOpen) return;

    // Check if click is outside the room dropdown
    if (this.dropdownRef.current && !this.dropdownRef.current.contains(event.target)) {
      this.setState({ dropdownOpen: false });
    }
  };

  toggleDropdown = () => {
    this.setState(prevState => ({ dropdownOpen: !prevState.dropdownOpen }));
  };

  componentDidUpdate(prevProps) {
    const { querystring } = this.props;
    const { querystring: prevQuerystring } = prevProps;
    const tenant = querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
    const prevTenant =
      prevQuerystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
    const isTenantChanged = tenant !== prevTenant;
    if (isTenantChanged) {
      this.setState({ tenantId: tenant }, () => {
        this.getCurrentAttendanceRateByLocation();
        this.getLineChartData();
      });
    }
  }

  updateDataByLocationType({
    locationType,
    chartData = null,
    tableData = null,
    filterData = null,
  }) {
    let data = {};
    if (locationType === LOCATION_TYPE.BRANCH) {
      const { branchData } = this.state;
      data = { ...branchData };
    } else if (locationType === LOCATION_TYPE.FLOOR) {
      const { floorData } = this.state;
      data = { ...floorData };
    } else if (locationType === LOCATION_TYPE.DEPARTMENT) {
      const { departmentData } = this.state;
      data = { ...departmentData };
    } else {
      return {};
    }
    if (chartData) {
      data = { ...data, chartData: chartData };
    }
    if (tableData) {
      data = { ...data, tableData: tableData };
    }
    if (filterData) {
      let newFilterData = data.filterData ?? {};
      Object.keys(filterData).forEach(key => {
        newFilterData = {
          ...newFilterData,
          [key]: filterData[key],
        };
      });
      data = { ...data, filterData: newFilterData };
    }
    if (locationType === LOCATION_TYPE.BRANCH) {
      return { branchData: data };
    } else if (locationType === LOCATION_TYPE.FLOOR) {
      return { floorData: data };
    } else if (locationType === LOCATION_TYPE.DEPARTMENT) {
      return { departmentData: data };
    } else {
      return {};
    }
  }

  getTableDataByLocationType({ locationType }) {
    if (locationType === LOCATION_TYPE.BRANCH) {
      const { branchData } = this.state;
      return branchData['tableData'];
    } else if (locationType === LOCATION_TYPE.FLOOR) {
      const { floorData } = this.state;
      return floorData['tableData'];
    } else if (locationType === LOCATION_TYPE.DEPARTMENT) {
      const { departmentData } = this.state;
      return departmentData['tableData'];
    } else {
      return [];
    }
  }

  getChartDataByLocationType({ locationType }) {
    if (locationType === LOCATION_TYPE.BRANCH) {
      const { branchData } = this.state;
      return branchData['chartData'];
    } else if (locationType === LOCATION_TYPE.FLOOR) {
      const { floorData } = this.state;
      return floorData['chartData'];
    } else if (locationType === LOCATION_TYPE.DEPARTMENT) {
      const { departmentData } = this.state;
      return departmentData['chartData'];
    } else {
      return [];
    }
  }

  getFilterDataByLocationType({ locationType }) {
    if (locationType === LOCATION_TYPE.BRANCH) {
      const { branchData } = this.state;
      return branchData['filterData'];
    } else if (locationType === LOCATION_TYPE.FLOOR) {
      const { floorData } = this.state;
      return floorData['filterData'];
    } else if (locationType === LOCATION_TYPE.DEPARTMENT) {
      const { departmentData } = this.state;
      return departmentData['filterData'];
    } else {
      return {};
    }
  }

  extractLocationInfo({ data, locationType }) {
    const locationIdSet = new Set();
    const idSet = new Set();
    data.forEach(obj => {
      const { branch_id, branch_name, floor_id, floor_name, department } = obj;
      if (locationType === LOCATION_TYPE.BRANCH) {
        if (!branch_id) {
          return;
        }
        if (!idSet.has(branch_id)) {
          idSet.add(branch_id);
          locationIdSet.add({ id: branch_id, name: branch_name });
        }
      } else if (locationType === LOCATION_TYPE.FLOOR) {
        if (!floor_id) {
          return;
        }
        if (!idSet.has(floor_id)) {
          idSet.add(floor_id);
          locationIdSet.add({ id: floor_id, name: floor_name });
        }
      } else if (locationType === LOCATION_TYPE.DEPARTMENT) {
        if (!department) {
          return;
        }
        if (!idSet.has(department)) {
          idSet.add(department);
          locationIdSet.add({ id: department, name: department });
        }
      }
    });
    const arr = Array.from(locationIdSet);
    for (let i = 0; i < arr.length; i++) {
      const color = LINE_CHART_COLOR_LIST[i];
      arr[i]['fillColor'] = color ?? getRandomHexColor();
    }
    return arr.reduce((acc, obj) => {
      const { id, name, fillColor } = obj;
      acc[id] = { id, name, fillColor };
      return acc;
    }, {});
  }

  getCurrentAttendanceRateByLocation = async () => {
    const { tenantId, selectedLocationType } = this.state;
    if (!tenantId || !selectedLocationType) {
      return;
    }
    this.setState({ isLoadingLocation: true });
    let res = [];
    let resLocationList = [];
    if (selectedLocationType === LOCATION_TYPE.BRANCH) {
      const tsRes = await fetchLatestTimestampListByHourForBranch({
        tenantId,
      });
      const latestTimestampVal = tsRes?.[0]?.timestamp;
      res = await fetchEmployeeAttendanceRateByHourForBranch({
        tenantId,
        fromDate: latestTimestampVal,
        toDate: latestTimestampVal,
      });
      res = res || [];
      // find a collection of records that has same latest timestamp
      const latestTsItemList = res.reduce((acc, obj) => {
        const { branch_id, timestamp, branch_name } = obj;
        obj['name'] = branch_name;
        if (!acc[branch_id] || acc[branch_id] < timestamp) {
          acc[branch_id] = obj;
        }
        return acc;
      }, {});
      resLocationList = Object.values(latestTsItemList);
      resLocationList.sort((a, b) => a.branch_order - b.branch_order);
    } else if (selectedLocationType === LOCATION_TYPE.FLOOR) {
      const tsRes = await fetchLatestTimestampListByHourForFloor({
        tenantId,
      });
      const latestTimestampVal = tsRes?.[0]?.timestamp;
      res = await fetchEmployeeAttendanceRateByHourForFloor({
        tenantId,
        fromDate: latestTimestampVal,
        toDate: latestTimestampVal,
      });
      res = res || [];
      // find a collection of records that has same latest timestamp
      const latestTsItemList = res.reduce((acc, obj) => {
        const { floor_id, timestamp, floor_name } = obj;
        obj['name'] = floor_name;
        if (!acc[floor_id] || acc[floor_id] < timestamp) {
          acc[floor_id] = obj;
        }
        return acc;
      }, {});
      resLocationList = Object.values(latestTsItemList);
      resLocationList.sort((a, b) => a.floor_order - b.floor_order);
    } else if (selectedLocationType === LOCATION_TYPE.DEPARTMENT) {
      const tsRes = await fetchLatestTimestampListByHourForDepartment({
        tenantId,
      });
      const latestTimestampVal = tsRes?.[0]?.timestamp;
      res = await fetchEmployeeAttendanceRateByHourForDepartment({
        tenantId,
        fromDate: latestTimestampVal,
        toDate: latestTimestampVal,
      });
      res = res || [];
      // find a collection of records that has same latest timestamp
      const latestTsItemList = res.reduce((acc, obj) => {
        const { department, timestamp } = obj;
        obj['name'] = department;
        if (!acc[department] || acc[department] < timestamp) {
          acc[department] = obj;
        }
        return acc;
      }, {});
      resLocationList = Object.values(latestTsItemList);
      resLocationList.sort((a, b) => a.name.localeCompare(b.name));
    }
    // Update attendance_rate
    resLocationList = resLocationList.map(item => {
      item.attendance_rate = round(item.attendance_rate * 100, 2);
      return item;
    });

    const newData = this.updateDataByLocationType({
      locationType: selectedLocationType,
      tableData: resLocationList,
    });

    this.setState({
      ...newData,
      isLoadingLocation: false,
    });
  };

  fillMissingData = ({ data }) => {
    const timestampSet = new Set();
    const dateSet = new Set();
    for (const chartData of data) {
      const { data } = chartData;
      data.forEach(item => {
        const { date, timestamp } = item;
        if (date) {
          dateSet.add(date);
        } else if (timestamp) {
          timestampSet.add(timestamp);
        }
      });
    }
    for (const chartData of data) {
      const { data } = chartData;
      const timestampValues = data.map(item => item.timestamp);
      const dateValues = data.map(item => item.date);
      // filter value that is in timeValueSet but not in currentTimeValues
      const missingTimeValues = Array.from(timestampSet).filter(
        item => !timestampValues.includes(item)
      );
      const missingDateValues = Array.from(dateSet).filter(
        item => !dateValues.includes(item)
      );
      if (missingTimeValues.length > 0) {
        for (const missingTimeValue of missingTimeValues) {
          data.push({
            timestamp: missingTimeValue,
            date: '',
            attendance_rate: null,
          });
        }
      }
      if (missingDateValues.length > 0) {
        for (const missingDateValue of missingDateValues) {
          data.push({
            date: missingDateValue,
            timestamp: '',
            attendance_rate: null,
          });
        }
      }
      data.sort((a, b) => {
        const { date: dateA, timestamp: timestampA } = a;
        const { date: dateB, timestamp: timestampB } = b;
        if (dateA && dateB) {
          return compareAsc(new Date(dateA), new Date(dateB));
        } else if (timestampA && timestampB) {
          return compareAsc(new Date(timestampA), new Date(timestampB));
        }
        return 0;
      });
    }
    return data;
  };

  getLineChartData = async () => {
    const { tenantId, selectedLocationType } = this.state;
    if (!tenantId || !selectedLocationType) {
      return;
    }
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const {
      selectedHistoryType,
      selectedDate,
      fromSelectedDate,
      toSelectedDate,
      selectedTimeRange,
      locationOptions,
    } = filterData;

    this.setState({ isLoadingLineChart: true });
    let res = [];
    if (selectedHistoryType === HISTORY_TYPE.DAY) {
      const fromSelectedDateStr = format(fromSelectedDate, 'yyyy-MM-dd');
      const toSelectedDateStr = format(toSelectedDate, 'yyyy-MM-dd');
      if (selectedLocationType === LOCATION_TYPE.BRANCH) {
        res = await fetchEmployeeAttendanceRateByDayForBranch({
          tenantId,
          fromDate: fromSelectedDateStr,
          toDate: toSelectedDateStr,
        });
      } else if (selectedLocationType === LOCATION_TYPE.FLOOR) {
        res = await fetchEmployeeAttendanceRateByDayForFloor({
          tenantId,
          fromDate: fromSelectedDateStr,
          toDate: toSelectedDateStr,
        });
      } else if (selectedLocationType === LOCATION_TYPE.DEPARTMENT) {
        res = await fetchEmployeeAttendanceRateByDayForDepartment({
          tenantId,
          fromDate: fromSelectedDateStr,
          toDate: toSelectedDateStr,
        });
      }
    } else if (selectedHistoryType === HISTORY_TYPE.HOUR) {
      const { fromTime, toTime } = selectedTimeRange;
      const selectedDateString = format(selectedDate, 'yyyy-MM-dd');
      const fromDate = `${selectedDateString} ${fromTime}:00`;
      const toDate = `${selectedDateString} ${toTime}:00`;
      if (selectedLocationType === LOCATION_TYPE.BRANCH) {
        res = await fetchEmployeeAttendanceRateByHourForBranch({
          tenantId,
          fromDate,
          toDate,
        });
      } else if (selectedLocationType === LOCATION_TYPE.FLOOR) {
        res = await fetchEmployeeAttendanceRateByHourForFloor({
          tenantId,
          fromDate,
          toDate,
        });
      } else if (selectedLocationType === LOCATION_TYPE.DEPARTMENT) {
        res = await fetchEmployeeAttendanceRateByHourForDepartment({
          tenantId,
          fromDate,
          toDate,
        });
      }
    }
    res = res || [];

    const locationInfoMap = this.extractLocationInfo({
      data: res,
      locationType: selectedLocationType,
    });
    let newLineChartData = [];
    for (const key in locationInfoMap) {
      if (Object.hasOwnProperty.call(locationInfoMap, key)) {
        const locationId = locationInfoMap[key]['id'];
        const locationName = locationInfoMap[key]['name'];
        let filteredData = res
          .filter(item => {
            if (selectedLocationType === LOCATION_TYPE.BRANCH) {
              return item.branch_id === locationId;
            } else if (selectedLocationType === LOCATION_TYPE.FLOOR) {
              return item.floor_id === locationId;
            } else if (selectedLocationType === LOCATION_TYPE.DEPARTMENT) {
              return item.department === locationId;
            }
            return false;
          })
          .map(item => ({
            timestamp: item.timestamp ?? '',
            date: item.date ?? '',
            attendance_rate: round(item.attendance_rate * 100, 2),
          }));
        newLineChartData.push({
          name: locationName,
          locationId: locationId,
          data: filteredData,
          fillColor: locationInfoMap[key]['fillColor'],
        });
      }
    }

    // Only fill missing data for smaller date ranges to avoid performance issues
    const shouldFillMissingData = selectedHistoryType === HISTORY_TYPE.HOUR ||
      (selectedHistoryType === HISTORY_TYPE.DAY && res.length < 500);

    if (shouldFillMissingData) {
      newLineChartData = this.fillMissingData({ data: newLineChartData });
    }

    // Update line multiselect options
    const newLineOptions = newLineChartData
      .map(item => ({
        name: item?.name?.toString().toUpperCase(),
        code: item?.locationId,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    let newFilterData = null;
    if (locationOptions.includes(-1)) {
      newFilterData = {
        ...filterData,
        locationOptions: newLineOptions,
        selectedLocations: newLineOptions,
      };
    }
    const newData = this.updateDataByLocationType({
      locationType: selectedLocationType,
      chartData: newLineChartData,
      filterData: newFilterData,
    });

    this.setState({
      ...newData,
      isLoadingLineChart: false,
    });
  };

  datePickerOnChanged = (e, field) => {
    const BOUNCE_IN_MILISECONDS = 300;
    const { selectedLocationType } = this.state;
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const { fromSelectedDate } = filterData;
    let isSelectedDateField = false;
    if (
      field === DATE_PICKER_FIELD.TO_DATE &&
      isBefore(e.value, fromSelectedDate)
    ) {
      isSelectedDateField = true;
    }
    if (!isSameDay(e.value, this.state[field])) {
      const debouncedFunc = debounce(() => {
        this.getLineChartData();
      }, BOUNCE_IN_MILISECONDS);
      if (this.pendingRequest) {
        // Cancel any pending requests
        clearTimeout(this.pendingRequest);
      }
      // Schedule the debounced function to run after 500ms
      this.pendingRequest = setTimeout(() => {
        debouncedFunc();
        this.pendingRequest = null;
      }, BOUNCE_IN_MILISECONDS);
    }

    let newFilterData = {};
    if (isSelectedDateField) {
      newFilterData = {
        [field]: e.value,
        [DATE_PICKER_FIELD.FROM_DATE]: e.value,
      };
    } else {
      newFilterData = {
        [field]: e.value,
      };
    }

    const newData = this.updateDataByLocationType({
      locationType: selectedLocationType,
      filterData: newFilterData,
    });

    this.setState({
      ...newData,
    });
  };

  renderDatePicker() {
    const { selectedLocationType, isLoadingLineChart } = this.state;
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const { selectedDate } = filterData;
    const maxDate = new Date();
    return (
      <CustomCalendar
        disabled={isLoadingLineChart}
        value={selectedDate}
        onChange={e =>
          this.datePickerOnChanged(e, DATE_PICKER_FIELD.SELECTED_DATE)
        }
        maxDate={maxDate}
        label=""
      ></CustomCalendar>
    );
  }

  renderDateRangePicker() {
    const { selectedLocationType, isLoadingLineChart } = this.state;
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const { fromSelectedDate, toSelectedDate } = filterData;
    const maxDate = new Date();
    return (
      <Fragment>
        <CustomCalendar
          className="mr-2"
          disabled={isLoadingLineChart}
          value={fromSelectedDate}
          onChange={e =>
            this.datePickerOnChanged(e, DATE_PICKER_FIELD.FROM_DATE)
          }
          maxDate={toSelectedDate}
          label={COMMON_TEXT.FROM_DATE}
        ></CustomCalendar>
        <CustomCalendar
          disabled={isLoadingLineChart}
          value={toSelectedDate}
          onChange={e => this.datePickerOnChanged(e, DATE_PICKER_FIELD.TO_DATE)}
          maxDate={maxDate}
          label={COMMON_TEXT.TO_DATE}
        ></CustomCalendar>
      </Fragment>
    );
  }

  renderFromDatePicker() {
    const { selectedLocationType, isLoadingLineChart } = this.state;
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const { fromSelectedDate, toSelectedDate } = filterData;
    return (
      <CustomCalendar
        disabled={isLoadingLineChart}
        value={fromSelectedDate}
        onChange={e =>
          this.datePickerOnChanged(e, DATE_PICKER_FIELD.FROM_DATE)
        }
        maxDate={toSelectedDate}
        label=""
      />
    );
  }

  renderToDatePicker() {
    const { selectedLocationType, isLoadingLineChart } = this.state;
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const { toSelectedDate } = filterData;
    const maxDate = new Date();
    return (
      <CustomCalendar
        disabled={isLoadingLineChart}
        value={toSelectedDate}
        onChange={e => this.datePickerOnChanged(e, DATE_PICKER_FIELD.TO_DATE)}
        maxDate={maxDate}
        label=""
      />
    );
  }

  timeRangeOnChanged = (e, type) => {
    let newVal = e.value;
    const { selectedLocationType } = this.state;
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const { selectedTimeRange } = filterData;
    const { fromTime, toTime } = selectedTimeRange;
    let newFilterData = {};
    if (type === 'fromTime') {
      if (newVal >= toTime) {
        const foundIndex = this.toTimeRangeOptions.findIndex(
          item => item.value === newVal
        );
        let newIndex = foundIndex + 1;
        newIndex =
          newIndex > this.timeRangeOptions.length - 1
            ? this.timeRangeOptions.length - 1
            : newIndex;
        const newToTime = this.toTimeRangeOptions[newIndex].value;
        newFilterData = {
          selectedTimeRange: {
            fromTime: newVal,
            toTime: newToTime,
          },
        };
      } else {
        newFilterData = {
          selectedTimeRange: {
            fromTime: newVal,
            toTime: toTime,
          },
        };
      }
    } else if (type === 'toTime') {
      if (newVal <= fromTime) {
        const foundIndex = this.toTimeRangeOptions.findIndex(
          item => item.value === newVal
        );
        let newIndex = foundIndex;
        newIndex = newIndex < 0 ? 0 : newIndex;
        const newFromTime = this.fromTimeRangeOptions[newIndex].value;
        newFilterData = {
          selectedTimeRange: {
            fromTime: newFromTime,
            toTime: newVal,
          },
        };
      } else {
        newFilterData = {
          selectedTimeRange: {
            fromTime: fromTime,
            toTime: newVal,
          },
        };
      }
    }

    const newData = this.updateDataByLocationType({
      locationType: selectedLocationType,
      filterData: newFilterData,
    });
    this.setState(
      {
        ...newData,
      },
      debounce(() => {
        this.getLineChartData();
      }, 500)
    );
  };

  renderDatePickerContainer() {
    const { selectedLocationType } = this.state;
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const { selectedHistoryType } = filterData;
    if (selectedHistoryType === HISTORY_TYPE.DAY) {
      return this.renderDateRangePicker();
    } else if (selectedHistoryType === HISTORY_TYPE.HOUR) {
      return (
        <>
          {this.renderDatePicker()}
          {this.renderTimeRangeDropdown()}
        </>
      );
    }
    return <></>;
  }

  renderTimeRangeDropdown() {
    const { selectedLocationType } = this.state;
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const { selectedTimeRange } = filterData;
    const { fromTime, toTime } = selectedTimeRange;
    return (
      <div className="timerange-select-container">
        <CustomDropdown
          label="開始"
          value={fromTime}
          options={this.fromTimeRangeOptions}
          onChange={e => this.timeRangeOnChanged(e, 'fromTime')}
        ></CustomDropdown>
        <CustomDropdown
          label="終了"
          value={toTime}
          options={this.toTimeRangeOptions}
          onChange={e => this.timeRangeOnChanged(e, 'toTime')}
        ></CustomDropdown>
      </div>
    );
  }

  renderStartTimeDropdown() {
    const { selectedLocationType } = this.state;
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const { selectedTimeRange } = filterData;
    const { fromTime } = selectedTimeRange;
    return (
      <CustomDropdown
        value={fromTime}
        options={this.fromTimeRangeOptions}
        onChange={e => this.timeRangeOnChanged(e, 'fromTime')}
      />
    );
  }

  renderEndTimeDropdown() {
    const { selectedLocationType } = this.state;
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const { selectedTimeRange } = filterData;
    const { toTime } = selectedTimeRange;
    return (
      <CustomDropdown
        value={toTime}
        options={this.toTimeRangeOptions}
        onChange={e => this.timeRangeOnChanged(e, 'toTime')}
      />
    );
  }

  handleLocationChange = (option) => {
    const { selectedLocationType } = this.state;
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const { selectedLocations } = filterData;

    const isSelected = selectedLocations.some(item => item.code === option.code);

    let newSelection;
    if (isSelected) {
      newSelection = selectedLocations.filter(item => item.code !== option.code);
    } else {
      newSelection = [...selectedLocations, option];
    }

    const newFilterData = {
      selectedLocations: newSelection,
    };
    const newData = this.updateDataByLocationType({
      locationType: selectedLocationType,
      filterData: newFilterData,
    });
    this.setState({
      ...newData,
    });
  };

  toggleDropdown = () => {
    this.setState(prevState => ({ dropdownOpen: !prevState.dropdownOpen }));
  };

  renderRoomCountDropdown() {
    const { isLoadingLocation, selectedLocationType, dropdownOpen } = this.state;
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const { selectedLocations, locationOptions } = filterData;

    const selectedCount = selectedLocations?.length || 0;
    const isDisabled = locationOptions?.length === 0 || isLoadingLocation;

    return (
      <div
        className={`room-dropdown ${isDisabled ? 'disabled' : ''}`}
        ref={this.dropdownRef}
        onClick={isDisabled ? null : this.toggleDropdown}
      >
        <span className="dropdown-label">
          部屋（{selectedCount}）
        </span>
        {/* <i className={`pi ${dropdownOpen ? 'pi-chevron-up' : 'pi-chevron-down'}`} /> */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="p-icon p-dropdown-trigger-icon p-clickable"
          aria-hidden="true"
          data-pr-overlay-visible="false"
          data-pc-section="dropdownicon"
        >
          <path d="M7.01744 10.398C6.91269 10.3985 6.8089 10.378 6.71215 10.3379C6.61541 10.2977 6.52766 10.2386 6.45405 10.1641L1.13907 4.84913C1.03306 4.69404 0.985221 4.5065 1.00399 4.31958C1.02276 4.13266 1.10693 3.95838 1.24166 3.82747C1.37639 3.69655 1.55301 3.61742 1.74039 3.60402C1.92777 3.59062 2.11386 3.64382 2.26584 3.75424L7.01744 8.47394L11.769 3.75424C11.9189 3.65709 12.097 3.61306 12.2748 3.62921C12.4527 3.64535 12.6199 3.72073 12.7498 3.84328C12.8797 3.96582 12.9647 4.12842 12.9912 4.30502C13.0177 4.48162 12.9841 4.662 12.8958 4.81724L7.58083 10.1322C7.50996 10.2125 7.42344 10.2775 7.32656 10.3232C7.22968 10.3689 7.12449 10.3944 7.01744 10.398Z" fill="white"></path>
        </svg>

        {dropdownOpen && !isDisabled && (
          <div className="dropdown-menu">
            {locationOptions.map(option => {
              const isChecked = selectedLocations.some(item => item.code === option.code);
              return (
                <div
                  key={option.code}
                  className="dropdown-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.handleLocationChange(option);
                  }}
                >
                  <Checkbox
                    inputId={`room-${option.code}`}
                    checked={isChecked}
                    onChange={(e) => {
                      e.stopPropagation();
                      this.handleLocationChange(option);
                    }}
                  />
                  <label
                    htmlFor={`room-${option.code}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {option.name}
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  renderMessageError = () => {
    const { tenantId } = this.state;
    if (!tenantId) {
      return (
        <Message
          severity="error"
          text={COMMON_TEXT.TENANT_NOT_SELECTED_PLEASE_SELECT}
        />
      );
    }
  };

  renderLineChart({ chartData, filterData }) {
    const { isLoadingLineChart: isLoadingData } = this.state;
    const { selectedLocations, selectedHistoryType } = filterData;
    chartData = chartData ?? [];

    // If no locations selected, show all available data
    let displayItems;
    if (!selectedLocations || selectedLocations.length === 0) {
      displayItems = chartData;
    } else {
      displayItems = chartData.filter(item =>
        selectedLocations.some(
          locationItem => locationItem?.code === item.locationId
        )
      );
    }

    const sortedDisplayItems = displayItems.sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    const xAxisDataKey =
      selectedHistoryType === HISTORY_TYPE.DAY ? 'date' : 'timestamp';

    return (
      <div style={{
        position: 'relative',
        minHeight: '300px',
        background: '#252f55',
        border: 'none',
        borderRadius: '0',
        boxShadow: 'none',
        padding: '0',
        margin: '0'
      }}>
        {sortedDisplayItems.length === 0 ? (
          <div style={{
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'center',
            height: '300px',
            color: '#ffffff',
            fontSize: '14px'
          }}>
            データがありません
          </div>
        ) : (
          <CustomLineChart
            data={sortedDisplayItems}
            xAxisDataKey={xAxisDataKey}
            yAxisDataKey={'attendance_rate'}
            tooltipLabel={COMMON_TEXT.ATTENDANCE_RATE}
            yAxisLabel={'%'}
            yAxisMaxValue={100}
            yAxisMinValue={0}
            containerHeight={300}
            isLoading={isLoadingData}
            tickInterval={5}
          />
        )}
      </div>
    );
  }

  renderRefreshButton() {
    const { isLoadingLineChart, isLoadingLocation, tenantId } = this.state;
    return (
      <button
        className="refresh-button"
        disabled={isLoadingLineChart || isLoadingLocation || !tenantId}
        onClick={() => {
          this.getCurrentAttendanceRateByLocation();
          this.getLineChartData();
        }}
      >
        <i className="pi pi-refresh" />
      </button>
    );
  }

  locationTypeButtonOnChanged = e => {
    this.setState({ selectedLocationType: e.value }, () => {
      this.getCurrentAttendanceRateByLocation();
      this.getLineChartData();
    });
  };

  renderLocationTypeSelectButton = () => {
    const { selectedLocationType } = this.state;
    return (
      <div className="card flex justify-content-center">
        <SelectButton
          value={selectedLocationType}
          onChange={e => this.locationTypeButtonOnChanged(e)}
          options={this.locationTypeOptions}
          allowEmpty={false}
        />
      </div>
    );
  };

  historyTypeDropdownOnChange = e => {
    const { selectedLocationType } = this.state;
    const newFilterData = {
      selectedHistoryType: e.value,
    };
    const newData = this.updateDataByLocationType({
      locationType: selectedLocationType,
      filterData: newFilterData,
    });
    this.setState(
      {
        ...newData,
      },
      () => {
        this.getLineChartData();
      }
    );
  };

  renderHistoryTypeDropdown = () => {
    const { selectedLocationType } = this.state;
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const { selectedHistoryType } = filterData;
    return (
      <CustomDropdown
        value={selectedHistoryType}
        options={this.historyTypeOptions}
        onChange={e => this.historyTypeDropdownOnChange(e)}
      />
    );
  };

  renderHistoryControlsBasedOnType = () => {
    const { selectedLocationType } = this.state;
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    const { selectedHistoryType } = filterData;

    if (selectedHistoryType === HISTORY_TYPE.DAY) {
      // For 日別 (Daily): Show タイプ + から + まで
      return (
        <div className="control-row">
          <div className="control-group">
            <label className="control-label">タイプ</label>
            <div className="control-wrapper">
              {this.renderHistoryTypeDropdown()}
            </div>
          </div>
          <div className="control-group">
            <label className="control-label">から</label>
            <div className="control-wrapper">
              {this.renderFromDatePicker()}
            </div>
          </div>
          <div className="control-group">
            <label className="control-label">まで</label>
            <div className="control-wrapper">
              {this.renderToDatePicker()}
            </div>
          </div>
        </div>
      );
    } else {
      // For 時間別 (Hourly): Show タイプ + 日付 + 開始 + 終了
      return (
        <div className="control-row">
          <div className="control-group">
            <label className="control-label">タイプ</label>
            <div className="control-wrapper">
              {this.renderHistoryTypeDropdown()}
            </div>
          </div>
          <div className="control-group">
            <label className="control-label">日付</label>
            <div className="control-wrapper">
              {this.renderDatePicker()}
            </div>
          </div>
          <div className="control-group">
            <label className="control-label">開始</label>
            <div className="control-wrapper">
              {this.renderStartTimeDropdown()}
            </div>
          </div>
          <div className="control-group">
            <label className="control-label">終了</label>
            <div className="control-wrapper">
              {this.renderEndTimeDropdown()}
            </div>
          </div>
        </div>
      );
    }
  };

  LocationListView = () => {
    const { isLoadingLocation, selectedLocationType } = this.state;
    const currentLocationList = this.getTableDataByLocationType({
      locationType: selectedLocationType,
    });

    const infoButtonOnClick = e => {
      if (this.tooltipRef.current) {
        this.tooltipRef.current.show(e);
      }
    };

    const LegendContent = () => {
      return (
        <div className="legend-container">
          <div className="legend-header">
            <span>{COMMON_TEXT.ATTENDANCE_RATE_THRESHOLD}</span>
          </div>
          <div className="legend-content">
            {ATTENDANCE_RATE_LEGEND.map((item, index) => {
              const { color, label, value } = item;
              return (
                <div className="item-row" key={`legend-item-row-${index}`}>
                  <div className="item-name-container">
                    <div
                      className="item-color"
                      style={{ backgroundColor: color }}
                    ></div>
                    <div className="item-name">
                      <span>{label}</span>
                    </div>
                  </div>
                  <div className="item-value">
                    <span>{value}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <div className="location-list-container">
        <div className="title-container">
          <span>{COMMON_TEXT.LATEST_ATTENDANCE_RATE}</span>
          <Tooltip
            ref={this.tooltipRef}
            target={`.custom-legend-button`}
            position="right"
            className="custom-legend-tooltip"
            event="hover"
          >
            <div className="data-container-legend">
              {<LegendContent></LegendContent>}
            </div>
          </Tooltip>
          <Button
            type="button"
            className={`custom-legend-button custom-legend-button}`}
            severity="secondary"
            text
            onClick={e => infoButtonOnClick(e)}
          >
            <img
              src="/assets/img/btn_info.svg"
              alt="Info"
              width="18"
              height="18"
              style={{ pointerEvents: 'none' }}
            />
          </Button>
        </div>
        <div className="location-list">
          {isLoadingLocation ? (
            <div className="loading-container">
              <LoadingSpinner />
            </div>
          ) : currentLocationList.length === 0 ? (
            <>
              <div className="no-data-container">{COMMON_TEXT.NO_DATA}</div>
            </>
          ) : (
            currentLocationList.map((item, index) => {
              const {
                name,
                attendance_zone,
                attendance_rate,
                timestamp,
                current_counts,
                total_counts,
              } = item;
              return (
                <div
                  key={`loc-item-${generateRandomString(10)}`}
                  className={`location-item location-zone-${attendance_zone}`}
                >
                  <div className="left-container">
                    <div className="item-name">{name}</div>
                  </div>
                  <div className="right-container">
                    <div className="rate">
                      {`${attendance_rate}`}
                      <span className="percentage-symbol">{`%`}</span>
                    </div>
                    <div className="count">{`(${current_counts}/${total_counts})`}</div>
                  </div>
                  <div className="timestamp">
                    {format(new Date(timestamp), 'MM月dd日 - HH:mm')}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  ChartView = () => {
    const { selectedLocationType, isLoadingLineChart } = this.state;
    const chartData = this.getChartDataByLocationType({
      locationType: selectedLocationType,
    });
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });
    return (
      <div className="chart-view-container">
        <div className="title-container">{COMMON_TEXT.LOG}</div>
        <div className="chart-view">
          <div className="menu-container menu-container-1">
            {this.renderHistoryTypeDropdown()}
            {this.renderDatePickerContainer()}
          </div>
          <div className="menu-container mt-2">
            {this.renderRoomCountDropdown()}
          </div>
          <div className="chart-container">
            {isLoadingLineChart ? (
              <div className="loading-container">
                <LoadingSpinner />
              </div>
            ) : (
              this.renderLineChart({
                chartData,
                filterData,
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  getStatusColor = (attendanceRate) => {
    if (attendanceRate <= 30) return '#00B573'; // Green
    if (attendanceRate <= 50) return '#ADDE61'; // Light Green
    if (attendanceRate <= 70) return '#FFE135'; // Yellow
    if (attendanceRate <= 90) return '#FFA14F'; // Orange  
    return '#FF6E6E'; // Red
  };

  renderProgressBar = (attendanceRate) => {
    const percentage = Math.min(100, Math.max(0, attendanceRate));
    const color = this.getStatusColor(attendanceRate);

    return (
      <div className="attendance-progress-container">
        <div className="attendance-progress-background">
          <div
            className="attendance-progress-bar"
            style={{
              width: `${percentage}%`,
              backgroundColor: color
            }}
          />
        </div>
      </div>
    );
  };

  renderStatusIndicator = (attendanceRate) => {
    const color = this.getStatusColor(attendanceRate);
    return (
      <div
        className="status-indicator"
        style={{ backgroundColor: color }}
      />
    );
  };

  renderTabNavigation = () => {
    const { selectedLocationType } = this.state;

    return (
      <div className="tab-navigation">
        <div className="tab-spacer"></div>
        <div className="tab-container">
          <button
            className={`tab-button ${selectedLocationType === LOCATION_TYPE.BRANCH ? 'active' : ''}`}
            onClick={() => this.locationTypeButtonOnChanged({ value: LOCATION_TYPE.BRANCH })}
          >
            拠点
          </button>
          <button
            className={`tab-button ${selectedLocationType === LOCATION_TYPE.FLOOR ? 'active' : ''}`}
            onClick={() => this.locationTypeButtonOnChanged({ value: LOCATION_TYPE.FLOOR })}
          >
            フロア
          </button>
          <button
            className={`tab-button ${selectedLocationType === LOCATION_TYPE.DEPARTMENT ? 'active' : ''}`}
            onClick={() => this.locationTypeButtonOnChanged({ value: LOCATION_TYPE.DEPARTMENT })}
          >
            部署
          </button>
        </div>
        {this.renderRefreshButton()}
      </div>
    );
  };

  renderLatestAttendancePanel = () => {
    const { isLoadingLocation, selectedLocationType } = this.state;
    const currentLocationList = this.getTableDataByLocationType({
      locationType: selectedLocationType,
    });

    return (
      <div className="latest-attendance-panel">
        <div className="panel-header">
          <h2 className="panel-title">最新の出社率</h2>
          <Tooltip
            target={`.info-button`}
            position="right"
            className="custom-legend-tooltip"
            event="hover"
          >
            <div className="data-container-legend">
              <div className="legend-container">
                <div className="legend-header">
                  <span>{COMMON_TEXT.ATTENDANCE_RATE_THRESHOLD}</span>
                </div>
                <div className="legend-content">
                  {ATTENDANCE_RATE_LEGEND.map((item, index) => {
                    const { color, label, value } = item;
                    return (
                      <div className="item-row" key={`legend-item-row-${index}`}>
                        <div className="item-name-container">
                          <div
                            className="item-color"
                            style={{ backgroundColor: color }}
                          ></div>
                          <div className="item-name">
                            <span>{label}</span>
                          </div>
                        </div>
                        <div className="item-value">
                          <span>{value}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Tooltip>
          <button className="info-button">
            <img
              src="/assets/img/btn_info.svg"
              alt="Info"
              width="18"
              height="18"
              style={{ pointerEvents: 'none' }}
            />
          </button>
        </div>

        <div className="attendance-table-container">
          <div className="attendance-table-header">
            <div className="header-cell status-header">状態</div>
            <div className="header-cell department-header">部署名</div>
            <div className="header-cell rate-header">出社率</div>
            <div className="header-cell time-header">更新日時</div>
          </div>

          <div className="attendance-list">
            {isLoadingLocation ? (
              <div className="loading-container">
                <LoadingSpinner />
              </div>
            ) : currentLocationList.length === 0 ? (
              <div className="no-data-container">{COMMON_TEXT.NO_DATA}</div>
            ) : (
              currentLocationList.map((item, index) => {
                const {
                  name,
                  attendance_rate,
                  timestamp,
                } = item;

                return (
                  <div key={`attendance-item-${index}`} className="attendance-item">
                    <div className="status-cell">
                      {this.renderStatusIndicator(attendance_rate)}
                    </div>
                    <div className="department-cell">{name}</div>
                    <div className="rate-cell">
                      <span className="rate-text">{attendance_rate}%</span>
                      {this.renderProgressBar(attendance_rate)}
                    </div>
                    <div className="time-cell">
                      <div className="timestamp-line">
                        {format(new Date(timestamp), 'M月d日')}
                      </div>
                      <div className="timestamp-time">
                        {format(new Date(timestamp), 'H:mm')}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  renderHistoryPanel = () => {
    const { selectedLocationType, isLoadingLineChart } = this.state;
    const chartData = this.getChartDataByLocationType({
      locationType: selectedLocationType,
    });
    const filterData = this.getFilterDataByLocationType({
      locationType: selectedLocationType,
    });

    return (
      <div className="history-panel">
        <div className="panel-header">
          <h2 className="panel-title">履歴</h2>
        </div>

        <div className="history-controls">
          {this.renderHistoryControlsBasedOnType()}
          <div className="control-row">
            <div className="control-group locations-control">
              <div className="control-wrapper">
                {this.renderRoomCountDropdown()}
              </div>
            </div>
          </div>
        </div>

        <div className="chart-container">
          {isLoadingLineChart ? (
            <div className="loading-container">
              <LoadingSpinner />
            </div>
          ) : (
            this.renderLineChart({
              chartData,
              filterData,
            })
          )}
        </div>
      </div>
    );
  };

  render() {
    const { tenantId } = this.state;

    if (!tenantId) {
      return (
        <div className="employee-attendance-container">
          <Message
            severity="error"
            text={COMMON_TEXT.TENANT_NOT_SELECTED_PLEASE_SELECT}
          />
        </div>
      );
    }

    return (
      <div className="employee-attendance-container">
        {this.renderTabNavigation()}
        <div className="main-content">
          {this.renderLatestAttendancePanel()}
          {this.renderHistoryPanel()}
        </div>
      </div>
    );
  }
}

EmployeeAttendance.propTypes = {};

export default EmployeeAttendance;
